import React, { useState, useEffect } from "react";
import {
  makeStyles,
  useTheme,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  Email,
  Wifi,
  Info,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(1, 0),
  },
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "rgba(0, 229, 255, 0.25)",
      boxShadow: "0 8px 32px 0 rgba(0, 229, 255, 0.08)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    paddingBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.4rem",
  },
  infoBanner: {
    background: `${theme.palette.primary.main}0A`,
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  infoBannerIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.3rem",
    marginTop: 2,
    flexShrink: 0,
  },
  infoBannerText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  actionButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      color: "rgba(255, 255, 255, 0.3) !important",
      boxShadow: "none",
    },
  },
  outlineButton: {
    borderColor: `${theme.palette.primary.main}80`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 20px",
    fontSize: "0.88rem",
    transition: "all 0.25s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}14`,
      boxShadow: `0 0 10px ${theme.palette.primary.main}33`,
    },
    "&:disabled": {
      borderColor: "rgba(255, 255, 255, 0.15)",
      color: "rgba(255, 255, 255, 0.3)",
    },
  },
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 14,
    color: "#fff",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8)",
  },
  dialogTitle: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    padding: theme.spacing(2, 3),
    "& h2": {
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "1.15rem",
    },
  },
}));

// [ANTIGRAVITY - Plan 1127] Estandarización Dark Glassmorphic de EmailSettings
const EmailSettings = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { update } = useCompanySettings();

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");

  const [form, setForm] = useState({
    smtpHost: "",
    smtpPort: "587",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
  });

  useEffect(() => {
    if (settings) {
      setForm({
        smtpHost: settings.smtpHost || "",
        smtpPort: settings.smtpPort || "587",
        smtpUser: settings.smtpUser || "",
        smtpPass: settings.smtpPass || "",
        smtpFrom: settings.smtpFrom || "",
      });
      if (settings.smtpUser) {
        setTestEmailAddress(settings.smtpUser);
      }
    }
  }, [settings]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveField = async (field) => {
    await update({ column: field, data: form[field] });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await Promise.all([
        saveField("smtpHost"),
        saveField("smtpPort"),
        saveField("smtpUser"),
        saveField("smtpPass"),
        saveField("smtpFrom"),
      ]);
      toast.success(`✅ ${i18n.t("settings.email.successSave")}`);
      if (onSettingsChange) onSettingsChange({ ...settings, ...form });
    } catch (e) {
      toast.error(`${i18n.t("settings.email.errorTest")} Save`);
    }
    setLoading(false);
  };

  const handleOpenTestModal = () => {
    if (!form.smtpHost || !form.smtpUser || !form.smtpPass) {
      toast.warning(
        i18n.t(
          "settings.email.alertFillRequired",
          "Completa Host, Usuario y Contraseña antes de probar."
        )
      );
      return;
    }
    if (!testEmailAddress) {
      setTestEmailAddress(form.smtpUser);
    }
    setTestModalOpen(true);
  };

  const handleExecuteTest = async () => {
    if (!testEmailAddress) {
      toast.warning("Ingresa un correo destinatario.");
      return;
    }

    setTesting(true);
    try {
      const { data } = await api.post("/settings/email/test", {
        smtpHost: form.smtpHost,
        smtpPort: form.smtpPort,
        smtpUser: form.smtpUser,
        smtpPass: form.smtpPass,
        smtpFrom: form.smtpFrom,
        testEmailAddress: testEmailAddress,
        sendTestEmail: true,
      });

      if (data.ok) {
        toast.success(
          `✅ ${i18n.t("settings.email.testSuccess", "Conexión SMTP exitosa. Revisa la bandeja de: ")}${testEmailAddress}`
        );
        setTestModalOpen(false);
      } else {
        toast.error(
          `❌ ${i18n.t("settings.email.testError", "Error SMTP: ")}${data.error}`
        );
      }
    } catch (err) {
      toast.error(
        `❌ ${i18n.t("settings.email.testErrorConnect", "No se pudo conectar con el servidor. Verifica la configuración.")}`
      );
    }
    setTesting(false);
  };

  return (
    <div className={classes.root} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <Paper className={classes.card} elevation={0}>
        <div className={classes.sectionHeader}>
          <Email className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>
            {i18n.t(
              "settings.email.sections.smtpServer",
              "Servidor SMTP y Credenciales"
            )}
          </Typography>
        </div>

        <div className={classes.infoBanner}>
          <Info className={classes.infoBannerIcon} />
          <Typography
            className={classes.infoBannerText}
            dangerouslySetInnerHTML={{
              __html: i18n.t("settings.email.gmailHelper"),
            }}
          />
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label={i18n.t("settings.email.host")}
              placeholder="smtp.gmail.com"
              value={form.smtpHost}
              onChange={handleChange("smtpHost")}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label={i18n.t("settings.email.port", "Puerto")}
              placeholder="587"
              value={form.smtpPort}
              onChange={handleChange("smtpPort")}
              variant="outlined"
              size="small"
              fullWidth
              helperText={i18n.t(
                "settings.email.portHelper",
                "587 (TLS) · 465 (SSL)"
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={i18n.t("settings.email.user")}
              placeholder="clinica@gmail.com"
              value={form.smtpUser}
              onChange={handleChange("smtpUser")}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={i18n.t("settings.email.password")}
              placeholder="xxxx xxxx xxxx xxxx"
              type={showPass ? "text" : "password"}
              value={form.smtpPass}
              onChange={handleChange("smtpPass")}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPass((v) => !v)}
                      edge="end"
                      style={{ color: theme.palette.primary.main }}
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={i18n.t("settings.email.from")}
              placeholder="Clínica Dientes <clinica@gmail.com>"
              value={form.smtpFrom}
              onChange={handleChange("smtpFrom")}
              variant="outlined"
              size="small"
              fullWidth
              helperText={i18n.t("settings.email.fromHelper")}
            />
          </Grid>
        </Grid>

        <div className={classes.actions}>
          <Button
            variant="outlined"
            className={classes.outlineButton}
            startIcon={<Wifi />}
            onClick={handleOpenTestModal}
            disabled={loading || testing}
          >
            {i18n.t("settings.email.testConnection")}
          </Button>

          <Button
            variant="contained"
            className={classes.actionButton}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
            onClick={handleSave}
            disabled={loading || testing}
          >
            {loading
              ? i18n.t("settings.email.saving")
              : i18n.t("settings.email.save")}
          </Button>
        </div>
      </Paper>

      {/* DIÁLOGO MODAL PARA PROBAR CONEXIÓN SMTP (Reemplazo in-app de window.prompt) */}
      <Dialog
        open={testModalOpen}
        onClose={() => !testing && setTestModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {i18n.t("settings.email.testDialogTitle", "Probar Conexión SMTP")}
        </DialogTitle>
        <DialogContent
          dividers
          style={{ borderColor: "rgba(255, 255, 255, 0.08)", padding: "20px 24px" }}
        >
          <Typography
            variant="body2"
            style={{ color: "rgba(255, 255, 255, 0.75)", marginBottom: 16 }}
          >
            {i18n.t(
              "settings.email.testDialogDesc",
              "Ingresa una dirección de correo para enviar un mensaje de prueba y verificar que tu servidor SMTP funcione correctamente."
            )}
          </Typography>
          <TextField
            label={i18n.t(
              "settings.email.testEmailLabel",
              "Correo destinatario de prueba"
            )}
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="destinatario@ejemplo.com"
            disabled={testing}
            autoFocus
          />
        </DialogContent>
        <DialogActions
          style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            padding: "12px 24px",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setTestModalOpen(false)}
            variant="outlined"
            disabled={testing}
            style={{
              color: "#aaa",
              borderColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: 8,
              textTransform: "none",
            }}
          >
            {i18n.t("settings.email.btnClose", "Cerrar")}
          </Button>
          <Button
            onClick={handleExecuteTest}
            variant="contained"
            className={classes.actionButton}
            disabled={testing || !testEmailAddress}
            startIcon={
              testing ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {testing
              ? i18n.t("settings.email.testing", "Probando...")
              : i18n.t("settings.email.btnSendTest", "Enviar Prueba")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmailSettings;
